import { ExcelValidationError, NoSheetExistsError } from "../utils/ExcelValidationError";
import { getHeaderToIndexMap, getRowsWithProperties, getUsedRangeWithProperties } from "../utils/excel";
import { Ptc } from "./ptcParser";
import {
  ANALYSIS_SHEET_COLUMNS_AFTER_STH_JURISDICTIONS,
  ANALYSIS_SHEET_COLUMNS_BEFORE_STH_JURISDICTIONS,
  ANALYSIS_SHEET_REQUIRED_COLUMNS,
  ATIN_ID_COLUMN,
  ATIN_NAME_COLUMN,
  DEPRECATED_COLUMN,
  FINAL_PTC_COLUMN,
  PTC_NA_COLUMN,
  YES_STRING,
} from "../utils/constants";
import { range } from "lodash";
import { Atin } from "./sthAtinParser";
import { parseSthTaxabilityPattern } from "../utils/parsers";

export const parseAnalysisSheet = (
  worksheetName: string,
  tdmPtcNameToPtc: Map<string, Ptc>,
  setLoadingText: (loadingText: string) => void
) => {
  return Excel.run(async (context) => {
    setLoadingText("Reading Analysis sheet");

    const analysisSheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
    await context.sync();

    if (analysisSheet.isNullObject) {
      throw NoSheetExistsError(worksheetName);
    }

    const usedRange = await getUsedRangeWithProperties(context, analysisSheet, ["rowCount", "columnCount"]);

    const headerToIndex = await getHeaderToIndexMap(usedRange, context, worksheetName);
    for (const column of ANALYSIS_SHEET_REQUIRED_COLUMNS) {
      if (!headerToIndex.has(column)) {
        throw new ExcelValidationError(`Missing column: ${column}`, worksheetName);
      }
    }

    const sthJurisdictionColumnIndices = getSthJurisdictionColumnIndices(headerToIndex);

    const analysisSheetPtcs = new Map<string, Ptc>(tdmPtcNameToPtc);
    const rows = await getRowsWithProperties(usedRange, context, ["values"]);
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];

      const originalPtc = row.values[0][headerToIndex.get(PTC_NA_COLUMN)].toString().trim();
      const ptcName = row.values[0][headerToIndex.get(FINAL_PTC_COLUMN)].toString().trim();

      if (!analysisSheetPtcs.has(originalPtc)) {
        throw new ExcelValidationError(`PTC ${originalPtc} does not exist in TDM sheet`, worksheetName, i + 1);
      }

      if (ptcName) {
        if (analysisSheetPtcs.has(ptcName)) {
          analysisSheetPtcs.set(ptcName, {
            ...analysisSheetPtcs.get(ptcName),
            isUsedForSth: true,
          });
        } else {
          analysisSheetPtcs.set(ptcName, {
            ptcName: ptcName,
            baselineTaxability: analysisSheetPtcs.get(originalPtc).baselineTaxability,
            isNew: true,
            isUsedForSth: true,
            atinsMapped: [],
          });
        }
      }

      const finalPtc = ptcName ? analysisSheetPtcs.get(ptcName) : analysisSheetPtcs.get(originalPtc);

      const atin = parseAtin(row, headerToIndex, finalPtc);
      parseSthTaxabilityPattern(atin, row, sthJurisdictionColumnIndices, worksheetName, i + 1);

      finalPtc.atinsMapped.push(atin);
      row.untrack();
    }

    return analysisSheetPtcs;
  });
};

const parseAtin = (row: Excel.Range, headerToIndex: Map<string, number>, mappedPtc: Ptc): Atin => {
  const atinId = row.values[0][headerToIndex.get(ATIN_ID_COLUMN)].toString().trim();
  const atinName = row.values[0][headerToIndex.get(ATIN_NAME_COLUMN)].toString().trim();
  const atinIsDeprecated = row.values[0][headerToIndex.get(DEPRECATED_COLUMN)].toString().trim() === YES_STRING;
  return {
    atinId,
    atinName,
    mappedPtc,
    deprecated: atinIsDeprecated,
    secondaryTaxability: "",
  };
};

const getSthJurisdictionColumnIndices = (headerToIndex: Map<string, number>) => {
  const startIndex = ANALYSIS_SHEET_COLUMNS_BEFORE_STH_JURISDICTIONS.length;
  const lastIndex = headerToIndex.get(ANALYSIS_SHEET_COLUMNS_AFTER_STH_JURISDICTIONS[0]);
  return range(startIndex, lastIndex);
};
